/**
 * Hero carousel slide focus management
 *
 * The hero uses `effect: fade`, which stacks every slide at the same offset. Swiper
 * therefore reports all of them as visible, so its own a11y module cannot help here:
 * `handleFocus` short-circuits on `isActive || isVisible` and `scrollOnFocus` never
 * fires. Fade also force-sets `watchSlidesProgress: true` via the effect's
 * `overwriteParams`, so that branch cannot be opted out of. Without this module, CTAs
 * on hidden slides stay in the tab order behind the visible one, with no visible
 * focus indicator anywhere on screen.
 *
 * This keeps everything but the active slide out of the tab order and out of the
 * accessibility tree - the correct pattern for a stacked slideshow, where the
 * prev/next buttons and pagination bullets are how you reach the other slides.
 * Addresses WCAG 2.4.3 Focus Order and 2.4.7 Focus Visible.
 *
 * Deliberately scoped to the hero. Multi-slide content carousels (reviews, product
 * cards, and so on) use Swiper's `scrollOnFocus` instead, because there every slide
 * is genuinely reachable by tabbing and hiding them would remove content.
 */

const SUPPORTS_INERT = typeof HTMLElement !== "undefined" && "inert" in HTMLElement.prototype;

/**
 * Elements that are focusable by default, used only by the no-`inert` fallback.
 * @type {string}
 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button",
  "input",
  "select",
  "textarea",
  "details",
  "summary",
  "iframe",
  "object",
  "embed",
  "audio[controls]",
  "video[controls]",
  '[contenteditable]:not([contenteditable="false"])',
  "[tabindex]",
].join(",");

/** Marks a slide as currently withheld from focus, so it can be restored later. */
const HIDDEN_FLAG = "data-hero-focus-hidden";

/** Stores a descendant's original tabindex in the no-`inert` fallback. */
const ORIGINAL_TABINDEX = "data-hero-focus-tabindex";

/**
 * Remove a slide from the tab order and the accessibility tree
 * @param {HTMLElement} slide - Slide element to withhold from focus
 */
function hideSlideFromFocus(slide) {
  slide.setAttribute(HIDDEN_FLAG, "true");

  if (SUPPORTS_INERT) {
    // `inert` already removes the subtree from both the tab order and the
    // accessibility tree, so aria-hidden would be redundant here.
    if (!slide.inert) {
      slide.inert = true;
    }
    return;
  }

  slide.setAttribute("aria-hidden", "true");
  // Re-scanned on every pass so descendants added later are caught too.
  slide.querySelectorAll(FOCUSABLE_SELECTOR).forEach(node => {
    if (node.hasAttribute(ORIGINAL_TABINDEX)) return;
    const current = node.getAttribute("tabindex");
    node.setAttribute(ORIGINAL_TABINDEX, current === null ? "" : current);
    node.setAttribute("tabindex", "-1");
  });
}

/**
 * Return a slide to the tab order and the accessibility tree
 * @param {HTMLElement} slide - Slide element to restore
 */
function restoreSlideFocus(slide) {
  if (!slide.hasAttribute(HIDDEN_FLAG)) return;
  slide.removeAttribute(HIDDEN_FLAG);

  if (SUPPORTS_INERT) {
    slide.inert = false;
    return;
  }

  slide.removeAttribute("aria-hidden");
  slide.querySelectorAll(`[${ORIGINAL_TABINDEX}]`).forEach(node => {
    const original = node.getAttribute(ORIGINAL_TABINDEX);
    if (original) {
      node.setAttribute("tabindex", original);
    } else {
      node.removeAttribute("tabindex");
    }
    node.removeAttribute(ORIGINAL_TABINDEX);
  });
}

/**
 * Keep every non-active hero slide out of the tab order for the lifetime of a
 * Swiper instance. Safe to call more than once on the same instance.
 * @param {any} swiper - Swiper instance for a hero carousel
 * @returns {(() => void) | undefined} Function that forces an immediate
 *   recalculation, or undefined if management could not be attached
 */
export function attachHeroSlideFocus(swiper) {
  if (!swiper || !swiper.el) return undefined;
  if (swiper.el.dataset.heroFocusManaged === "true") return undefined;
  swiper.el.dataset.heroFocusManaged = "true";

  /** @type {number | null} */
  let frame = null;

  const exposeAllSlides = () => {
    (swiper.slides || []).forEach(slide => restoreSlideFocus(slide));
  };

  const update = () => {
    if (swiper.destroyed || !swiper.slides || !swiper.slides.length) return;

    // A single-slide hero has nothing to hide, and a disabled Swiper is not a
    // slideshow at all.
    if (swiper.enabled === false || swiper.slides.length === 1) {
      exposeAllSlides();
      return;
    }

    // Only the active slide is on screen with a stacked effect. Loop mode reorders
    // the real slides in the DOM rather than cloning them, so indexing into
    // `slides` stays correct as long as this re-runs on `loopFix`.
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) {
      exposeAllSlides();
      return;
    }

    const focused = document.activeElement;

    swiper.slides.forEach(slide => {
      if (slide === activeSlide) {
        restoreSlideFocus(slide);
        return;
      }

      // Never withhold the slide that currently holds focus - doing so drops focus
      // to the document body and loses the user's place. It gets re-evaluated as
      // soon as focus leaves.
      if (focused && slide.contains(focused)) {
        restoreSlideFocus(slide);
        return;
      }

      hideSlideFromFocus(slide);
    });
  };

  // Coalesced to at most one pass per frame, since several of these events fire
  // together around a single slide change.
  const schedule = () => {
    if (frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      update();
    });
  };

  swiper.on(
    [
      "slideChange",
      "slideChangeTransitionEnd",
      "transitionEnd",
      // Loop mode moves slides around in the DOM; activeIndex shifts with it.
      "loopFix",
      "update",
      "observerUpdate",
      "slidesLengthChange",
      "breakpoint",
      "enable",
      "disable",
    ].join(" "),
    schedule
  );

  swiper.el.addEventListener("focusout", schedule);

  swiper.on("beforeDestroy", () => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
    swiper.el.removeEventListener("focusout", schedule);
    delete swiper.el.dataset.heroFocusManaged;
    exposeAllSlides();
  });

  update();

  return update;
}

/**
 * Attach to any hero carousel that already has a live Swiper instance. Covers the
 * case where the hero initialized before this module finished loading.
 */
function attachToExistingHeroes() {
  document.querySelectorAll(".hero-carousel-wrapper .swiper__carousel").forEach(element => {
    const swiper = /** @type {any} */ (element).swiper;
    if (swiper) {
      attachHeroSlideFocus(swiper);
    }
  });
}

// hero.liquid announces its Swiper on init. Either order works: if this module
// loaded first the event attaches it, and if the hero initialized first the sweep
// below does. attachHeroSlideFocus is idempotent, so both firing is harmless.
document.addEventListener("hero:carousel-init", event => {
  const swiper = /** @type {any} */ (event).detail?.swiper;
  if (swiper) {
    attachHeroSlideFocus(swiper);
  }
});

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", attachToExistingHeroes);
} else {
  attachToExistingHeroes();
}

// Theme editor re-renders sections without a full page load.
document.addEventListener("shopify:section:load", attachToExistingHeroes);

window.attachHeroSlideFocus = attachHeroSlideFocus;
